import { Fragment, useCallback, useEffect, useLayoutEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { User } from "@supabase/supabase-js";
import { Link, useLocation } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Bell,
  ClipboardList,
  Clock,
  Database,
  Eye,
  FileText,
  Home,
  Hourglass,
  LifeBuoy,
  LogOut,
  Package,
  Plug,
  Printer,
  Receipt,
  Settings,
  ShieldAlert,
  ShoppingCart,
  Users,
  type LucideIcon,
} from "lucide-react";

import { GlobalPresencePanel } from "@/components/GlobalPresencePanel";
import { NotificationArrivalWindow } from "@/components/NotificationArrivalWindow";

import { useLaborActiveSessionContextForHubChrome } from "@/components/LaborActiveSessionProvider";
import { useAuth } from "@/components/AuthProvider";
import { canUseHubViewAs, isIntegratorAdmin, useIntegratorMe } from "@/lib/me-query";
import { setStandardUserPreview, standardUserPreviewActive } from "@/lib/standardUserPreview";
import { hubViewAsUserId, setHubViewAsUserId } from "@/lib/hubViewAs";
import { notificationsUnreadCountLiveQueryOptions } from "@/lib/notificationsUnreadCountLive";
import {
  clampQcLaborRemotePersistenceDisabled,
  effectiveLaborClockMenuStateForUserMenu,
  type LaborClockMenuState,
} from "@/pages/clampqc/clampQcLaborApi";
import { prePackLaborRemotePersistenceDisabled } from "@/pages/prepack/prepackLaborEnv";
import { hubLaborExpectedPageFromPathname } from "@/lib/hubLaborActiveSession";
import {
  readLaborDevCountdown,
  subscribeLaborDevCountdown,
  type LaborDevCountdownState,
} from "@/lib/labor/laborDevCountdown";
import {
  LABOR_DEV_SHELL_CHANGED_EVENT,
  LABOR_DEV_SHELL_STORAGE_KEY,
  readLaborDevShellLock,
} from "@/lib/labor/laborDevShellLock";
import { resolveLaborPresence } from "@/lib/labor/laborPresence";
import { releaseLaborHubPresence } from "@/lib/labor/laborReleaseHubPresence";
import { LaborStatusPanel } from "@/lib/labor/LaborStatusPanel";
import { useLaborDevStatusPanelSnapshot } from "@/lib/labor/laborDevStatusPanelContext";
import {
  laborRemotePersistenceDisabled,
  setLaborPersistenceOverride,
  setLaborShellLockdownBypassed,
  useLaborPersistenceOverride,
  useLaborShellLockdownBypass,
} from "@/lib/labor/laborEnv";
import { laborPageKeysEquivalent } from "@/lib/labor/laborPageKey";
import {
  isInboundReceiveDetailLaborPage,
  isInboundReceivingBrowseOnlyLaborPage,
} from "@/pages/shipping/inboundLaborPage";
import { useNowTick } from "@/lib/useNowTick";
import { apiFetch } from "@/lib/api";
import {
  hubOperationalDataSourceLabel,
  readHubOperationalDataSource,
  setHubOperationalDataSource,
  useHubOperationalDataSource,
} from "@/lib/hubDataSource";
import { navItemsForApp } from "@/lib/appNavigation";
import { cn } from "@/lib/utils";
import { registerViewportLayoutListeners } from "@/lib/useViewportLayoutEvents";
import { useHubReportIssue } from "@/components/HubReportIssueHost";
import { computeHorizontalMenuPanel, FLOATING_POPOVER_Z_INDEX, type AnchoredMenuPanelBox } from "@/lib/viewportAnchoredPopover";

function readStringMeta(user: User | undefined, key: string): string | null {
  if (!user?.user_metadata || typeof user.user_metadata !== "object") return null;
  const v = (user.user_metadata as Record<string, unknown>)[key];
  return typeof v === "string" && v.trim().length > 0 ? v.trim() : null;
}

function oauthAvatarUrl(user: User | undefined): string | null {
  if (!user) return null;
  return readStringMeta(user, "avatar_url") ?? readStringMeta(user, "picture");
}

function oauthFullName(user: User | undefined): string | null {
  if (!user) return null;
  return readStringMeta(user, "full_name") ?? readStringMeta(user, "name");
}

function initialsFromName(name: string): string | null {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
  }
  if (parts.length === 1) {
    const word = parts[0]!;
    if (word.length >= 2) return word.slice(0, 2).toUpperCase();
    if (word.length === 1) return (word + word).toUpperCase();
  }
  return null;
}

function initialsForUser(email: string, displayName: string | null, user: User | undefined): string {
  if (displayName) {
    const fromDisplay = initialsFromName(displayName);
    if (fromDisplay) return fromDisplay;
  }
  const fromOAuth = oauthFullName(user);
  if (fromOAuth) {
    const fromO = initialsFromName(fromOAuth);
    if (fromO) return fromO;
  }
  const local = email.split("@")[0] ?? "";
  const pieces = local.split(/[._-]+/).filter(Boolean);
  if (pieces.length >= 2) {
    return (pieces[0]![0]! + pieces[1]![0]!).toUpperCase();
  }
  return (local.slice(0, 2) || "?").toUpperCase();
}

const accountMenuItemClass =
  "flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 transition-colors hover:bg-slate-50";

/** Decorative only. A missing path still renders the catalog label. */
const ACCOUNT_MENU_ICONS: Record<string, LucideIcon> = {
  "/personal/home": Home,
  "/personal/notifications": Bell,
  "/personal/time-clock": Clock,
  "/personal/printing": Printer,
  "/personal/package-arrivals": Package,
  "/personal/purchase-requests": ShoppingCart,
  "/personal/reimbursements": Receipt,
  "/personal/document-requests": FileText,
  "/personal/dfm-change-review": ClipboardList,
  "/personal/safety-reports": ShieldAlert,
  "/personal/tickets": LifeBuoy,
  "/personal/chatgpt": Plug,
  "/personal/settings": Settings,
};

function formatCountdown(totalMs: number): string {
  const totalSeconds = Math.max(0, Math.ceil(totalMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

export function UserMenu() {
  const { session, signOut } = useAuth();
  const queryClient = useQueryClient();
  const location = useLocation();
  const meQuery = useIntegratorMe();
  const me = meQuery.data;
  const standardUserPreview = standardUserPreviewActive();
  const viewAsUserId = hubViewAsUserId();
  const canStartStandardUserPreview = canUseHubViewAs(me) || isIntegratorAdmin(me?.user);
  const viewAs = me?.viewAs ?? me?.user.viewAs ?? null;
  const [viewAsPickerOpen, setViewAsPickerOpen] = useState(false);
  const [viewAsSearch, setViewAsSearch] = useState("");
  const unreadCountQuery = useQuery({
    ...notificationsUnreadCountLiveQueryOptions,
    // Unread count seeds on the hub live stream connected snapshot; no boot GET.
    enabled: false,
  });
  const unreadCount = unreadCountQuery.data?.unreadCount ?? 0;
  const unreadBadgeLabel = unreadCount > 99 ? "99+" : String(unreadCount);
  const personalAccountLinks = useMemo(() => navItemsForApp("personal"), []);
  const [open, setOpen] = useState(false);
  const viewAsOptionsQuery = useQuery({
    queryKey: ["me", "view-as-options"],
    queryFn: () => apiFetch<{ users: Array<{ id: string; displayName: string | null; email: string | null }> }>("/api/me/view-as-options"),
    enabled: canStartStandardUserPreview && open && viewAsPickerOpen,
  });
  const [peoplePanelOpen, setPeoplePanelOpen] = useState(false);
  const { openSupportTicketFromMenu } = useHubReportIssue();
  const [imageFailed, setImageFailed] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuSurfaceRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  const [menuBox, setMenuBox] = useState<AnchoredMenuPanelBox | null>(null);
  const [laborDevCountdown, setLaborDevCountdown] = useState<LaborDevCountdownState | null>(() =>
    readLaborDevCountdown(),
  );
  const [laborDevShellEpoch, setLaborDevShellEpoch] = useState(0);
  const laborPersistenceOverride = useLaborPersistenceOverride();
  const laborShellLockdownBypassed = useLaborShellLockdownBypass();
  const hubOperationalDataSource = useHubOperationalDataSource();
  const [simulationResetBusy, setSimulationResetBusy] = useState(false);
  const [simulationResetMessage, setSimulationResetMessage] = useState<string | null>(null);

  useEffect(() => {
    const onShellChanged = (): void => {
      setLaborDevShellEpoch((value) => value + 1);
    };
    const onStorage = (event: StorageEvent): void => {
      if (event.key !== null && event.key !== LABOR_DEV_SHELL_STORAGE_KEY) return;
      setLaborDevShellEpoch((value) => value + 1);
    };
    window.addEventListener(LABOR_DEV_SHELL_CHANGED_EVENT, onShellChanged);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(LABOR_DEV_SHELL_CHANGED_EVENT, onShellChanged);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const laborDevPanelProps = useLaborDevStatusPanelSnapshot();

  const repositionAccountMenu = useCallback(() => {
    if (!open || !triggerRef.current) {
      setMenuBox(null);
      return;
    }
    const menuWidth =
      import.meta.env.DEV && laborDevPanelProps != null ? Math.round(20 * 16) : Math.round(16 * 16);
    setMenuBox(
      computeHorizontalMenuPanel({
        anchorRect: triggerRef.current.getBoundingClientRect(),
        menuWidth,
        horizontalAlign: "end",
        gap: 4,
        maxHeightCap: Math.min(560, Math.floor(window.innerHeight * 0.9)),
      }),
    );
  }, [open, laborDevPanelProps]);

  const expectedLaborPage = useMemo(
    () => hubLaborExpectedPageFromPathname(location.pathname, location.search),
    [location.pathname, location.search],
  );

  const laborSurfaceContext = useMemo(() => {
    if (location.pathname.startsWith("/clamp-qc")) return "clamp" as const;
    if (location.pathname.startsWith("/shipping/pre-pack")) return "prepack" as const;
    return "other" as const;
  }, [location.pathname]);

  const laborPersistenceDisabledForCurrentSurface =
    laborSurfaceContext === "clamp"
      ? clampQcLaborRemotePersistenceDisabled()
      : laborSurfaceContext === "prepack"
        ? prePackLaborRemotePersistenceDisabled()
        : laborRemotePersistenceDisabled();
  const laborWritesEnabledForCurrentSurface = !laborPersistenceDisabledForCurrentSurface;

  const { openPayload, openSession, isFetched: laborOpenSessionFetched } =
    useLaborActiveSessionContextForHubChrome();

  const laborMenuPayload = useMemo((): Record<string, unknown> | undefined => {
    if (!laborOpenSessionFetched || openPayload === undefined) {
      return undefined;
    }
    void laborDevShellEpoch;
    const presence = resolveLaborPresence({
      openSession,
      devShell: readLaborDevShellLock(),
      activeOnOtherPage: openPayload?.active_on_other_page,
      activeOnOtherSource: openPayload?.active_on_other_source,
    });
    if (presence === null) {
      return { session: null };
    }
    if (laborPageKeysEquivalent(presence.page, expectedLaborPage)) {
      return {
        session: {
          page: presence.page,
          id: presence.source === "server" ? presence.serverSessionId : -1,
        } as Record<string, unknown>,
      };
    }
    if (
      isInboundReceivingBrowseOnlyLaborPage(expectedLaborPage)
      && isInboundReceiveDetailLaborPage(presence.page)
    ) {
      return { session: null };
    }
    return {
      session: null,
      active_on_other_page: presence.page,
    };
  }, [laborOpenSessionFetched, openPayload, openSession, expectedLaborPage, laborDevShellEpoch]);

  const laborClockState = useMemo((): LaborClockMenuState => {
    return effectiveLaborClockMenuStateForUserMenu({
      queryIsSuccess: laborOpenSessionFetched,
      payload: laborMenuPayload,
      expectedLaborPage,
      remotePersistenceDisabled: laborPersistenceDisabledForCurrentSurface,
    });
  }, [laborOpenSessionFetched, laborMenuPayload, expectedLaborPage, laborPersistenceDisabledForCurrentSurface]);

  const user = session?.user;
  const email = user?.email ?? "";
  const displayName =
    (me?.user.displayName && me.user.displayName.trim()) || oauthFullName(user) || null;
  const avatarUrl =
    (me?.user.avatarUrl && me.user.avatarUrl.trim()) ||
    (imageFailed ? null : oauthAvatarUrl(user));
  const initials = initialsForUser(email, me?.user.displayName?.trim() ?? null, user);

  const showLaborHourglass = laborClockState.kind !== "none";
  const devCountdownActive =
    import.meta.env.DEV &&
    laborDevCountdown !== null &&
    laborDevCountdown.isWorking &&
    laborPageKeysEquivalent(laborDevCountdown.page, expectedLaborPage);
  const devCountdownNow = useNowTick(devCountdownActive);
  const devCountdownRemainingMs =
    devCountdownActive && laborDevCountdown !== null
      ? laborDevCountdown.lastDbHeartbeatAtMs + laborDevCountdown.idleTimeoutMs - devCountdownNow
      : 0;
  const showDevCountdown = devCountdownActive && devCountdownRemainingMs > 0;
  /** Next heartbeat interval tick will POST (or apply the dev mirror) because attention moved after the last flush. */
  const devHeartbeatPendingAtNextPulse =
    devCountdownActive &&
    laborDevCountdown !== null &&
    laborDevCountdown.lastActivityMs > laborDevCountdown.lastDbHeartbeatAtMs;

  useEffect(() => {
    setImageFailed(false);
  }, [user?.id, me?.user.avatarUrl, avatarUrl]);

  useEffect(() => subscribeLaborDevCountdown(setLaborDevCountdown), []);

  useLayoutEffect(() => {
    if (!open) {
      setMenuBox(null);
      return;
    }
    repositionAccountMenu();
    const onLayout = (): void => {
      repositionAccountMenu();
    };
    return registerViewportLayoutListeners(onLayout);
  }, [open, repositionAccountMenu]);

  const onDocMouseDown = useCallback((event: MouseEvent) => {
    if (
      rootRef.current?.contains(event.target as Node) ||
      menuSurfaceRef.current?.contains(event.target as Node)
    ) {
      return;
    }
    setOpen(false);
  }, []);

  useEffect(() => {
    if (!open) return;
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [open, onDocMouseDown]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  if (!user || !email) {
    return null;
  }

  return (
    <div ref={rootRef} className="relative flex flex-shrink-0">
      <div className="relative inline-flex flex-shrink-0">
        <button
          ref={triggerRef}
          type="button"
          className={cn(
            "flex h-9 w-9 flex-shrink-0 items-center justify-center overflow-hidden rounded-full border border-slate-200 bg-slate-100 text-slate-600",
            "transition-colors hover:border-slate-300 hover:bg-slate-200",
            "focus:outline-none",
            !open && "focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2",
          )}
          aria-expanded={open}
          aria-haspopup="menu"
          aria-controls={listId}
          onClick={() => setOpen((value) => !value)}
          aria-label={
            unreadCount > 0
              ? `Account menu, ${email}, ${unreadBadgeLabel} unread notifications`
              : `Account menu, ${email}`
          }
        >
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt=""
              className="h-full w-full object-cover"
              referrerPolicy="no-referrer"
              onError={() => setImageFailed(true)}
            />
          ) : (
            <span className="text-xs font-semibold" aria-hidden>
              {initials}
            </span>
          )}
        </button>
        {showDevCountdown ? (
          <span
            className={cn(
              "pointer-events-none absolute -bottom-1 -right-2 z-30 rounded-full border border-white px-1.5 py-0.5 font-mono text-[10px] font-bold leading-none text-white shadow-md",
              devHeartbeatPendingAtNextPulse ? "bg-emerald-600" : "bg-amber-500",
            )}
            aria-hidden
            title={
              devHeartbeatPendingAtNextPulse
                ? "Dev labor idle countdown — next heartbeat pulse will flush activity to last_heartbeat_at"
                : "Dev labor idle timeout countdown (no pending heartbeat until you interact again)"
            }
          >
            {formatCountdown(devCountdownRemainingMs)}
          </span>
        ) : showLaborHourglass ? (
          <span
            className={cn(
              "pointer-events-none absolute -bottom-0.5 -right-0.5 z-20 flex h-5 w-5 items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-slate-200",
              laborClockState.kind === "this_page" ? "text-emerald-600" : "text-red-600",
            )}
            aria-hidden
          >
            <Hourglass className="h-3 w-3" strokeWidth={2.5} />
          </span>
        ) : null}
        {unreadCount > 0 ? (
          <span
            className="pointer-events-none absolute -right-1 -top-1 z-30 flex min-h-[1.125rem] min-w-[1.125rem] items-center justify-center rounded-full bg-rose-600 px-1 text-[10px] font-bold leading-none text-white shadow-sm ring-2 ring-white"
            aria-hidden
          >
            {unreadBadgeLabel}
          </span>
        ) : null}
      </div>
      {open && menuBox
        ? createPortal(
            <div
              ref={menuSurfaceRef}
              id={listId}
              role="menu"
              className="overflow-y-auto rounded-md border border-slate-200 bg-white py-1 text-left shadow-lg"
              style={{
                position: "fixed",
                top: menuBox.top,
                left: menuBox.left,
                width: menuBox.width,
                maxHeight: menuBox.maxHeight,
                zIndex: FLOATING_POPOVER_Z_INDEX,
              }}
            >
          <div className="border-b border-slate-100 px-3 py-2.5" role="none">
            {displayName ? (
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-slate-900">{displayName}</p>
                <p className="mt-0.5 truncate text-xs text-slate-500">{email}</p>
              </div>
            ) : (
              <p className="truncate text-sm text-slate-900">{email}</p>
            )}
            {laborClockState.kind !== "none" ? (
              <p className="mt-2 text-xs leading-snug text-slate-700">
                You are clocked in at: {laborClockState.locationLabel}.
              </p>
            ) : null}
          </div>
          {import.meta.env.DEV && laborDevPanelProps != null ? (
            <div className="border-b border-slate-100 px-3 py-2" role="none">
              <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                Development labor
              </p>
              <LaborStatusPanel {...laborDevPanelProps} />
            </div>
          ) : null}
          {import.meta.env.DEV ? (
            <div className="border-b border-slate-100 py-1" role="none">
              <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                Development labor writes
              </div>
              <button
                type="button"
                role="menuitem"
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 transition-colors hover:bg-slate-50"
                onClick={() => {
                  setLaborPersistenceOverride(
                    laborWritesEnabledForCurrentSurface ? "writes-disabled" : "writes-enabled",
                  );
                }}
              >
                <Database className="h-4 w-4 text-slate-500" aria-hidden />
                {laborWritesEnabledForCurrentSurface ? "Disable labor writing" : "Enable labor writing"}
              </button>
              {laborPersistenceOverride !== "default" ? (
                <button
                  type="button"
                  role="menuitem"
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-slate-500 transition-colors hover:bg-slate-50"
                  onClick={() => {
                    setLaborPersistenceOverride("default");
                  }}
                >
                  <span className="h-4 w-4" aria-hidden />
                  Use environment default
                </button>
              ) : null}
              <p className="px-3 pb-2 text-xs leading-snug text-slate-500">
                Currently {laborWritesEnabledForCurrentSurface ? "writing to labor tables" : "using local timers only"}.
              </p>
              <button
                type="button"
                role="menuitem"
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 transition-colors hover:bg-slate-50"
                onClick={() => {
                  setLaborShellLockdownBypassed(!laborShellLockdownBypassed);
                }}
              >
                <Hourglass className="h-4 w-4 text-slate-500" aria-hidden />
                {laborShellLockdownBypassed ? "Restore clock-in lockdown" : "Bypass clock-in lockdown"}
              </button>
              {laborShellLockdownBypassed ? (
                <p className="px-3 pb-2 text-xs leading-snug text-amber-700">
                  Hub pages stay reachable while clocked in. Production behavior is unchanged.
                </p>
              ) : null}
            </div>
          ) : null}
          {import.meta.env.DEV ? (
            <div className="border-b border-slate-100 py-1" role="none">
              <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                Operational data source
              </div>
              <button
                type="button"
                role="menuitem"
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 transition-colors hover:bg-slate-50"
                onClick={() => {
                  const next = readHubOperationalDataSource() === "simulation" ? "supabase" : "simulation";
                  setHubOperationalDataSource(next);
                  setSimulationResetMessage(null);
                }}
              >
                <Database className="h-4 w-4 text-slate-500" aria-hidden />
                {hubOperationalDataSource === "simulation"
                  ? "Use Supabase dev database"
                  : "Use simulation database (local)"}
              </button>
              <button
                type="button"
                role="menuitem"
                disabled={simulationResetBusy}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50"
                onClick={() => {
                  if (
                    !window.confirm(
                      "Reset the local simulation database? All simulation operational data will be truncated and re-seeded.",
                    )
                  ) {
                    return;
                  }
                  setSimulationResetBusy(true);
                  setSimulationResetMessage(null);
                  void apiFetch<{ ok?: boolean; error?: string }>("/api/dev/simulation/reset", {
                    method: "POST",
                  })
                    .then(() => {
                      setSimulationResetMessage("Simulation database reset completed.");
                    })
                    .catch((error: unknown) => {
                      const message = error instanceof Error ? error.message : String(error);
                      setSimulationResetMessage(message);
                    })
                    .finally(() => {
                      setSimulationResetBusy(false);
                    });
                }}
              >
                <span className="h-4 w-4" aria-hidden />
                {simulationResetBusy ? "Resetting simulation…" : "Reset simulation database"}
              </button>
              <p className="px-3 pb-2 text-xs leading-snug text-slate-500">
                Currently {hubOperationalDataSourceLabel(hubOperationalDataSource)}.
                {simulationResetMessage ? ` ${simulationResetMessage}` : null}
              </p>
            </div>
          ) : null}
          {personalAccountLinks.map((item) => {
            const Icon = ACCOUNT_MENU_ICONS[item.to] ?? FileText;
            return (
              <Fragment key={item.to}>
                {item.to === "/personal/settings" ? (
                  <button
                    type="button"
                    role="menuitem"
                    className={accountMenuItemClass}
                    onClick={() => {
                      setOpen(false);
                      setPeoplePanelOpen(true);
                    }}
                  >
                    <Users className="h-4 w-4 text-slate-500" aria-hidden />
                    People
                  </button>
                ) : null}
                {item.to === "/personal/tickets" ? (
                  <button
                    type="button"
                    role="menuitem"
                    className={accountMenuItemClass}
                    onClick={() => {
                      setOpen(false);
                      openSupportTicketFromMenu();
                    }}
                  >
                    <LifeBuoy className="h-4 w-4 text-slate-500" aria-hidden />
                    New support ticket
                  </button>
                ) : null}
                <Link
                  to={item.to}
                  role="menuitem"
                  className={accountMenuItemClass}
                  onClick={() => setOpen(false)}
                >
                  <Icon className="h-4 w-4 text-slate-500" aria-hidden />
                  {item.to === "/personal/notifications" ? (
                    <span className="flex min-w-0 flex-1 items-center justify-between gap-2">
                      <span>{item.label}</span>
                      {unreadCount > 0 ? (
                        <span className="rounded-full bg-rose-100 px-1.5 py-0.5 text-[10px] font-semibold text-rose-800">
                          {unreadBadgeLabel}
                        </span>
                      ) : null}
                    </span>
                  ) : (
                    item.label
                  )}
                </Link>
              </Fragment>
            );
          })}
          {canStartStandardUserPreview || standardUserPreview ? (
            <button
              type="button"
              role="menuitem"
              className={accountMenuItemClass}
              onClick={() => setStandardUserPreview(!standardUserPreview)}
            >
              <Eye className="h-4 w-4 text-slate-500" aria-hidden />
              {standardUserPreview ? "Exit standard user preview" : "Preview as standard user"}
            </button>
          ) : null}
          {canStartStandardUserPreview || viewAs || viewAsUserId ? (
            <div className="border-t border-slate-100 px-3 py-2">
              <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">View as</div>
              {viewAs ? (
                <button
                  type="button"
                  className="mb-2 w-full rounded-md border border-amber-300 bg-amber-50 px-2 py-1.5 text-left text-sm text-amber-950"
                  onClick={() => setHubViewAsUserId(null)}
                >
                  Exit {viewAs.displayName || viewAs.email || "this user"}
                </button>
              ) : (
                <button
                  type="button"
                  className="mb-2 text-sm text-brand-700 hover:underline"
                  onClick={() => setViewAsPickerOpen((openPicker) => !openPicker)}
                >
                  {viewAsPickerOpen ? "Hide people" : "Choose someone"}
                </button>
              )}
              {viewAsPickerOpen && !viewAs ? (
                <div className="space-y-2">
                  <input
                    value={viewAsSearch}
                    onChange={(event) => setViewAsSearch(event.target.value)}
                    placeholder="Search name or email"
                    className="h-8 w-full rounded-md border border-slate-300 px-2 text-sm"
                  />
                  <div className="max-h-48 overflow-y-auto rounded-md border border-slate-200">
                    {(viewAsOptionsQuery.data?.users ?? [])
                      .filter((person) => {
                        const haystack = `${person.displayName ?? ""} ${person.email ?? ""}`.toLowerCase();
                        return viewAsSearch.trim() === "" || haystack.includes(viewAsSearch.trim().toLowerCase());
                      })
                      .slice(0, 40)
                      .map((person) => (
                        <button
                          key={person.id}
                          type="button"
                          className="block w-full px-2 py-1.5 text-left text-sm text-slate-800 hover:bg-slate-50"
                          onClick={() => setHubViewAsUserId(person.id)}
                        >
                          <span className="block font-medium">{person.displayName || person.email || person.id}</span>
                          {person.displayName && person.email ? (
                            <span className="block text-[11px] text-slate-500">{person.email}</span>
                          ) : null}
                        </button>
                      ))}
                    {viewAsOptionsQuery.isLoading ? (
                      <p className="px-2 py-2 text-xs text-slate-500">Loading people…</p>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
          <button
            type="button"
            role="menuitem"
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 transition-colors hover:bg-slate-50"
            onClick={() => {
              setOpen(false);
              void (async () => {
                await releaseLaborHubPresence(queryClient);
                await signOut();
              })();
            }}
          >
            <LogOut className="h-4 w-4 text-slate-500" aria-hidden />
            Sign out
          </button>
            </div>,
            document.body,
          )
        : null}
      <NotificationArrivalWindow anchorRef={triggerRef} />
      <GlobalPresencePanel
        open={peoplePanelOpen}
        onOpenChange={setPeoplePanelOpen}
        anchorRef={triggerRef}
        showTrigger={false}
      />
      {viewAs
        ? createPortal(
            <button
              type="button"
              className="fixed bottom-3 left-1/2 z-[100] -translate-x-1/2 rounded-full border border-amber-400 bg-amber-100 px-4 py-2 text-sm font-semibold text-amber-950 shadow-lg"
              onClick={() => setHubViewAsUserId(null)}
            >
              Viewing as {viewAs.displayName || viewAs.email} · Exit
            </button>,
            document.body,
          )
        : standardUserPreview
        ? createPortal(
            <button
              type="button"
              className="fixed bottom-3 left-1/2 z-[100] -translate-x-1/2 rounded-full border border-amber-400 bg-amber-100 px-4 py-2 text-sm font-semibold text-amber-950 shadow-lg"
              onClick={() => setStandardUserPreview(false)}
            >
              Standard user preview active · Exit
            </button>,
            document.body,
          )
        : null}
    </div>
  );
}
